import { Clock, Effect } from 'effect';

import {
  defineEffectDocumentPersistence,
  makeDocumentPersistenceError,
  type BlobKind,
  type BlobRecord,
  type DefinedEffectDocumentPersistence,
  type DocumentPersistenceError,
  type DocumentPersistenceOperation,
  type DocumentRecord,
  type LeaseRecord,
  type PersistenceOptions,
} from './types.js';

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

function cloneDocument(document: DocumentRecord): DocumentRecord {
  return {
    ...document,
    spec: { ...document.spec },
    meta: { ...document.meta },
    graph: { ...document.graph },
  };
}

function cloneBlob(blob: BlobRecord): BlobRecord {
  return {
    ...blob,
    bytes: new Uint8Array(blob.bytes),
  };
}

function blobKey(documentId: string, kind: BlobKind, name: string): string {
  return `${documentId}\0${kind}\0${name}`;
}

function attemptSync<A>(
  operation: DocumentPersistenceOperation,
  evaluate: () => A,
): Effect.Effect<A, DocumentPersistenceError> {
  return Effect.try({
    try: evaluate,
    catch: (cause) => makeDocumentPersistenceError(operation, cause),
  });
}

/**
 * Process-local store. Flue's in-memory default: fine for tests and a single
 * Node process that does not need restart survival. Use Postgres when state
 * must survive replacement.
 */
export function createMemoryPersistence(
  options: PersistenceOptions = {},
): DefinedEffectDocumentPersistence {
  const documents = new Map<string, DocumentRecord>();
  const blobs = new Map<string, BlobRecord>();
  const leases = new Map<string, LeaseRecord>();

  const now = (
    operation: DocumentPersistenceOperation,
  ): Effect.Effect<number, DocumentPersistenceError> =>
    options.clock ? attemptSync(operation, () => options.clock!.now()) : Clock.currentTimeMillis;

  return defineEffectDocumentPersistence({
    migrateEffect: () => Effect.void,
    closeEffect: () =>
      attemptSync('close', () => {
        documents.clear();
        blobs.clear();
        leases.clear();
      }),
    putDocumentEffect: (input) =>
      Effect.gen(function* () {
        const nowMs = yield* now('put-document');
        const currentTime = yield* attemptSync('put-document', () => iso(nowMs));
        return yield* attemptSync('put-document', () => {
          const existing = documents.get(input.id);
          const record: DocumentRecord = {
            id: input.id,
            ownerUserId: input.ownerUserId,
            orgId: input.orgId,
            projectId: input.projectId,
            isPublished: input.isPublished,
            spec: { ...input.spec },
            meta: { ...input.meta },
            graph: { ...input.graph },
            phase: input.phase,
            createdAt: existing?.createdAt ?? input.createdAt ?? currentTime,
            updatedAt: input.updatedAt ?? currentTime,
          };
          documents.set(record.id, record);
          return cloneDocument(record);
        });
      }),
    getDocumentEffect: (id) =>
      attemptSync('get-document', () => {
        const found = documents.get(id);
        return found ? cloneDocument(found) : null;
      }),
    listDocumentsByOwnerEffect: (ownerUserId) =>
      attemptSync('list-documents-by-owner', () =>
        [...documents.values()]
          .filter((document) => document.ownerUserId === ownerUserId)
          .map(cloneDocument),
      ),
    putBlobEffect: (blob) =>
      attemptSync('put-blob', () => {
        if (!documents.has(blob.documentId)) {
          throw new Error(`document ${blob.documentId} does not exist`);
        }
        blobs.set(blobKey(blob.documentId, blob.kind, blob.name), cloneBlob(blob));
      }),
    getBlobEffect: (documentId, kind, name) =>
      attemptSync('get-blob', () => {
        const found = blobs.get(blobKey(documentId, kind, name));
        return found ? cloneBlob(found) : null;
      }),
    acquireLeaseEffect: (documentId, instanceId, ttlMs) =>
      Effect.gen(function* () {
        yield* attemptSync('acquire-lease', () => {
          if (!documents.has(documentId)) {
            throw new Error(`document ${documentId} does not exist`);
          }
        });
        const currentTime = yield* now('acquire-lease');
        return yield* attemptSync('acquire-lease', () => {
          const current = leases.get(documentId);
          if (
            current &&
            current.instanceId !== instanceId &&
            Date.parse(current.expiresAt) > currentTime
          ) {
            return false;
          }
          leases.set(documentId, {
            documentId,
            instanceId,
            expiresAt: iso(currentTime + ttlMs),
          });
          return true;
        });
      }),
    releaseLeaseEffect: (documentId, instanceId) =>
      attemptSync('release-lease', () => {
        const current = leases.get(documentId);
        if (current?.instanceId === instanceId) {
          leases.delete(documentId);
        }
      }),
    getLeaseEffect: (documentId) =>
      Effect.gen(function* () {
        const current = yield* attemptSync('get-lease', () => leases.get(documentId));
        if (!current) return null;
        const currentTime = yield* now('get-lease');
        return yield* attemptSync('get-lease', () => {
          if (Date.parse(current.expiresAt) <= currentTime) {
            leases.delete(documentId);
            return null;
          }
          return { ...current };
        });
      }),
  });
}
